import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import ArticlePage from 'pages/Article';
import CaregiverDossierPage from 'pages/CaregiverDossierPage';
import ConversationPage from 'pages/ConversationPage';
import FaqPage from 'pages/FaqPage';
import Home from 'pages/Home';
import InfoPage from 'pages/Info';
import InfoCategoryPage from 'pages/InfoCategoryPage';
import OctobreRosePage from 'pages/OctobreRose';
import MedicalProfileQuestionnaire from 'pages/MedicalProfileQuestionnaire';
import MessagingHowto from 'pages/MessagingHowto';
import NewConversation from 'pages/NewConversation';
import OnboardingPage from 'pages/Onboarding';
import OnboardingQuestionnaire from 'pages/OnboardingQuestionnaire';
import PasswordResetPage from 'pages/PasswordReset';
import PasswordResetSendEmailPage from 'pages/PasswordResetSendEmail';
import PatientConversationsHistory from 'pages/PatientConversationsHistory';
import PatientDocumentsPage from 'pages/PatientDocumentsPage';
import PatientHistoricConversation from 'pages/PatientHistoricConversation';
import RegisterPage from 'pages/Register';
import SettingsPage from 'pages/SettingsPage';
import SymptomTracking from 'pages/SymptomTracking';
import WelcomePage from 'pages/Welcome';
import posthog from 'posthog-js';
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { conversationActions, loadDraftsFromStorage } from 'store/conversations';
import Platform from 'utils/Platform';
import LoginPage from './pages/Login';
import MessagingHome from './pages/MessagingHome';
import NotFoundPage from './pages/shared/NotFoundPage';
import Shell from './pages/shared/Shell';
import UnauthorizedPage from './pages/shared/UnauthorizedPage';
import { routes } from './services/RouteService';

/*
 * The living style guide. Lazily imported so it costs the production bundle
 * nothing until someone actually asks for it — this ships inside a Capacitor
 * binary, where every kilobyte is app size. Nothing links to it, and the
 * route itself isn't registered in production builds (see below).
 */
const DesignSystemPage = lazy(() => import('pages/DesignSystem'));

/**
 * One page instance per category: opening a category from another one
 * mounts a fresh page (at the top, unfiltered), and going back restores the
 * previous one with its own filter and scroll position from the history entry.
 */
function InfoCategoryRoute() {
  const { slug } = useParams();
  return <InfoCategoryPage key={slug} />;
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    loadDraftsFromStorage().then(drafts => {
      dispatch(conversationActions.loadPersistedDrafts(drafts));
    });
  }, [dispatch]);

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href.replace(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g,
        '<uuid>',
      ),
    });
  }, [location]);

  useEffect(() => {
    CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const slug = event.url.split('gyngercare.com').pop();
      if (slug) {
        navigate(slug);
      }
      // If no match, do nothing - let regular routing logic take over
    });
  }, []);

  useEffect(() => {
    if (Platform.isNative()) {
      LiveUpdate.ready().then(() => {});
    }
  }, []);

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path={routes.home.route} element={<Home />} />
        <Route path={routes.messagingHome.route} element={<MessagingHome />} />
        <Route path={routes.messagingHowto.route} element={<MessagingHowto />} />
        <Route path={routes.conversation.route} element={<ConversationPage />} />
        <Route path={routes.patientDocuments.route} element={<PatientDocumentsPage />} />
        <Route path={routes.caregiverDossier.route} element={<CaregiverDossierPage />} />
        <Route path={routes.patientConversationsHistory.route} element={<PatientConversationsHistory />} />
        <Route path={routes.patientHistoricConversation.route} element={<PatientHistoricConversation />} />
        <Route path={routes.newChat.route} element={<NewConversation />} />
        <Route path={routes.info.route} element={<InfoPage />} />
        <Route path={routes.infoCategory.route} element={<InfoCategoryRoute />} />
        <Route path={routes.octobreRose.route} element={<OctobreRosePage />} />
        <Route path={routes.article.route} element={<ArticlePage />} />
        <Route path={routes.faq.route} element={<FaqPage />} />
        <Route path={routes.symptomTracking.route} element={<SymptomTracking />} />
        <Route path={routes.settings.route} element={<SettingsPage />} />
        <Route path={routes.medicalProfile.route} element={<MedicalProfileQuestionnaire />} />
      </Route>

      <Route path={routes.welcome.route} element={<WelcomePage />} />
      <Route path={routes.onboarding.route} element={<OnboardingPage />} />
      <Route path={routes.onboardingQuestionnaire.route} element={<OnboardingQuestionnaire />} />
      <Route path={routes.passwordResetSendEmail.route} element={<PasswordResetSendEmailPage />} />
      <Route path={routes.passwordReset.route} element={<PasswordResetPage />} />
      <Route path={routes.login.route} element={<LoginPage />} />
      <Route path={routes.register.route} element={<RegisterPage />} />
      {import.meta.env.MODE !== 'production' && (
        <Route
          path={routes.designSystem.route}
          element={
            <Suspense fallback={null}>
              <DesignSystemPage />
            </Suspense>
          }
        />
      )}
      <Route path={routes.notFound.route} element={<NotFoundPage />} />
      <Route path={routes.unauthorized.route} element={<UnauthorizedPage />} />
    </Routes>
  );
}

export default App;
